import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { Tab as IdentityVerifyUploadTab } from '@/components/organisms/self-checkin/identity-verify/tab'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { IdentityVerifyCamera } from '@/components/organisms/self-checkin/identity-verify/identity-verify-camera'
import { getIdentificationRequirement, IdentificationRequirement } from '@/libs/self-checkin'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { PaxSelect } from '@/components/molecules/pax-select'
import { countryList } from '@/libs/nationality'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type ContainerProps = {
  title: string
}

export const UploadPassport: React.FC<ContainerProps> = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const nationalities = countryList(i18n.language, false)

  const [representativePassportImage, setRepresentativePassportImage] = useState<string>()
  const [representativePassportObjectKey, setRepresentativePassportObjectKey] = useState<string | undefined>()
  const [nationality, setNationality] = useState<string | undefined>()
  const [detectNationality, setDetectNationality] = useState<string | undefined>()
  const [companionImageArray, setCompanionImageArray] = useState<
    {
      id: number
      companionPassportImage: string
      objectKey: string
    }[]
  >([])
  const [activateCameraCompanionIndex, setActivateCameraCompanionIndex] = useState<number>()
  const [companionNumber, setCompanionNumber] = useState<number>(
    (state.accommodationInfo?.paxTotal || state.selectReservation?.paxTotal || 1) - 1,
  )
  const [activateCameraType, setActivateCameraType] = useState<'representativePassport' | 'companionPassport' | undefined>(undefined)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)

  const uploadImage = (imageType: 'representativePassport' | 'companionPassport', index?: number): void => {
    setActivateCameraType(imageType)
    setActivateCameraCompanionIndex(index)
  }

  const onShutter = (image: any, nationality: string | undefined, objectKey: string) => {
    if (activateCameraType === 'representativePassport') {
      setRepresentativePassportImage(image)
      setRepresentativePassportObjectKey(objectKey)
      setNationality(nationality)
      setDetectNationality(nationality)
    } else if (activateCameraCompanionIndex !== undefined) {
      const photoInState = companionImageArray.findIndex(v => v.id === activateCameraCompanionIndex)
      if (photoInState !== -1) {
        companionImageArray[photoInState].companionPassportImage = image
        companionImageArray[photoInState].objectKey = objectKey
        setCompanionImageArray([...companionImageArray])
      } else {
        setCompanionImageArray([
          ...companionImageArray,
          {
            id: activateCameraCompanionIndex,
            companionPassportImage: image,
            objectKey,
          },
        ])
      }
    }
    setActivateCameraType(undefined)
  }

  const isDisabled = (): boolean => {
    if (representativePassportImage && companionNumber === 0) {
      return false
    }

    if (representativePassportImage && companionImageArray.length) {
      for (let index = 0; index < companionNumber; index++) {
        const image = companionImageArray.find(img => img.id === index)
        if (image) continue

        return true
      }

      return false
    }

    return true
  }

  const submit = () => {
    saveState({
      ...state,
      uploadLicense: {
        nationality,
        representativePassportImage,
        representativePassportObjectKey,
        companionImageArray,
      },
    })
    history.push({ pathname: '/self-checkin/confirm' })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }

    if (getIdentificationRequirement(state, state.accommodationInfo?.nationality) === IdentificationRequirement.None) {
      history.replace({ pathname: '/self-checkin/confirm' })
      return
    }
    setIsInitializeComponent(true)
    if (state.uploadLicense?.representativePassportImage) {
      setNationality(state.uploadLicense.nationality)
      setRepresentativePassportImage(state.uploadLicense.representativePassportImage)
      setRepresentativePassportObjectKey(state.uploadLicense.representativePassportObjectKey)
    }
    if (state.uploadLicense?.companionImageArray) {
      setCompanionImageArray(state.uploadLicense.companionImageArray)
    }
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <SelfCheckinLayout
      state={state}
      fullscreen={Boolean(activateCameraType)}
      header={!activateCameraType ? { goToPreviousPage: history.goBack, title: t('Identification') } : undefined}
      footer={!activateCameraType ? { right: { isDisabled: isDisabled(), label: t('Next'), onClick: submit } } : undefined}
    >
      {!activateCameraType && (
        <div>
          <div css={settingHeaderContainerStyle}>
            <IdentityVerifyUploadTab currentTab="passport" />
          </div>
          <div css={settingListContainerStyle}>
            <div css={descriptionStyle}>{t('Please upload your passport')}</div>
            <div css={uploadWrapperStyle}>
              <div style={{ display: 'flex' }}>
                <div style={{ width: '50%' }}>
                  <div css={imageTitleStyle}>{t('Representative')}</div>
                  <div css={uploadItemStyle} onClick={() => uploadImage('representativePassport')}>
                    <div css={representativePassportImage ? withBackgroundStyle : undefined}>
                      <img
                        css={identificationImageStyle}
                        src={representativePassportImage ? representativePassportImage : require('@/static/images/passport.svg')}
                        alt={t('Check-in with Identification')}
                      />
                    </div>
                    <div className={representativePassportImage ? 'uploaded' : ''} css={uploadItemTextStyle}>
                      <div>
                        <img
                          src={
                            representativePassportImage
                              ? require('@/static/images/camera_icon_white.svg')
                              : require('@/static/images/camera_icon_orange.svg')
                          }
                          alt={t('Take a Photo')}
                        />
                        <p>{t('Take a Photo')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {representativePassportImage && (
                  <div>
                    <div css={imageTitleStyle}>{t('Nationality')}</div>
                    {detectNationality && <div>{t('Detected from your passport')}</div>}
                    <select
                      style={{ marginTop: 16 }}
                      css={selectBoxStyle}
                      value={nationality}
                      onChange={e => setNationality(e.target.value)}
                    >
                      <option value={''}>{t('Country name')}</option>
                      {nationalities.map((country, index) => (
                        <option value={country.value} key={index}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div css={imageTitleStyle}>{t('Companion')}</div>
              <div css={numberOfPeopleWrapperStyle}>
                <PaxSelect isEnabledZero value={`${companionNumber}`} onClick={val => setCompanionNumber(Number(val))}></PaxSelect>
              </div>
              <div css={companionNumber % 3 === 2 ? leftCompanionPassportImageWrapperStyle : companionPassportImageWrapperStyle}>
                {new Array(companionNumber).fill(null).map((_, i) => {
                  const image = companionImageArray.find(img => img.id == i)
                  return (
                    <div key={i}>
                      <div css={uploadItemStyle} onClick={() => uploadImage('companionPassport', i)} key={`companion${i}`}>
                        <div css={companionImageArray.length && image ? withBackgroundStyle : undefined}>
                          <img
                            css={identificationImageStyle}
                            src={
                              companionImageArray.length && image ? image.companionPassportImage : require('@/static/images/passport.svg')
                            }
                            alt={t('Check-in with Identification')}
                          />
                        </div>
                        <div className={companionImageArray.length && image ? 'uploaded' : ''} css={uploadItemTextStyle}>
                          <div>
                            <img
                              src={
                                companionImageArray.length && image
                                  ? require('@/static/images/camera_icon_white.svg')
                                  : require('@/static/images/camera_icon_orange.svg')
                              }
                              alt={t('Take a Photo')}
                            />
                            <p>{t('Take a Photo')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {activateCameraType && (
        <IdentityVerifyCamera onCancel={() => setActivateCameraType(undefined)} onShutter={onShutter} type="passport" />
      )}
    </SelfCheckinLayout>
  )
}

const descriptionStyle = css(
  {
    paddingTop: 40,
    color: '#272727',
    fontWeight: 'bold',
    fontSize: 22,
    lineHeight: '30px',
    letterSpacing: '1.1px',
    textAlign: 'center',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 26, lineHeight: '35px', letterSpacing: '1.3px' },
  }),
)

const imageTitleStyle = css(
  {
    fontSize: 20,
    marginBottom: 16,
    marginTop: 32,
    fontWeight: 'bold',
    color: '#272727',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 24 },
  }),
)

const uploadWrapperStyle = css(
  {
    margin: '0 auto',
    padding: '0 59px',
    display: 'block',
  },
  createSelfCheckinResponsiveStyle({
    portrait: { padding: '0 40px' },
    landscape13: { padding: '0 50px' },
  }),
)

const uploadItemStyle = css(
  {
    width: 296,
    height: 206,
    cursor: 'pointer',
    color: '#f2a40b',
    fontSize: 18,
    fontWeight: 'bold',
    letterSpacing: '2.7px',
    lineHeight: '27px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 0,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { width: 357, height: 248 },
    landscape13: { fontSize: 22 },
  }),
)

const withBackgroundStyle = css(
  {
    position: 'relative',
    '&::after': {
      content: '""',
      height: 206,
      width: '100%',
      background: 'rgba(0, 0, 0, 0.4)',
      top: 0,
      left: 0,
      position: 'absolute',
      borderRadius: 10,
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { '&::after': { height: 248 } },
  }),
)

const identificationImageStyle = css(
  {
    width: 296,
    height: 206,
    borderRadius: 8,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { width: 357, height: 248 },
  }),
)

const uploadItemTextStyle = css(
  {
    width: 296,
    height: 69,
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    paddingBottom: 14,
    p: {
      height: 20,
      fontSize: 14,
    },
    img: {
      width: 21,
      height: 18,
    },
    '&.uploaded': {
      color: '#fff',
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { width: 357 },
    landscape13: { p: { fontSize: 18 } },
  }),
)

const settingListContainerStyle = css(
  {
    borderRadius: '0 0 10px 10px',
    background: '#FFF',
    paddingBottom: 24,
    minHeight: 763,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { borderRadius: 0, minHeight: 839 },
    landscape13: { minHeight: 763, paddingBottom: 15 },
  }),
)

const settingHeaderContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const companionPassportImageWrapperStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'top',
  justifyContent: 'start',
  gap: 24,
})

const leftCompanionPassportImageWrapperStyle = css(
  {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'top',
    justifyContent: 'start',
    gap: 24,
    '&::after': {
      content: '"" !important',
      display: 'block',
      width: 296,
      height: 206,
      left: 0,
      top: 0,
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { '&::after': { width: 357, height: 248 } },
  }),
)

const numberOfPeopleWrapperStyle = css(
  {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { marginBottom: 24 },
    landscape13: { marginBottom: 24 },
  }),
)

const selectBoxStyle = css({
  width: 200,
  height: '61.21px',
  border: '1px solid #cccccc',
  color: '#272727',
  borderRadius: '40px',
  fontSize: '21px',
  padding: '14px 35px',
  appearance: 'none',
  backgroundImage: `url(${require('@/static/images/arrow_down_yellow.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '95% center',
  backgroundSize: 14,
  backgroundColor: '#FFFFFF',
  display: 'block',
})
